import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
// import H from '@here/maps-api-for-javascript';
import { RoutePlanning } from '../../../../../interfaces/route-planning/@route-planning.interface';
import { MapService } from '../../../../../services/map/map.service';
import { Section } from '../../../../../interfaces/route-planning/section.interface';
import { MAP_ICONS } from '../../../../../constants/map-icons.constants';
import { eTransportMode } from '../../../../../enums/route-planning/transport-mode.enum';
import { Location } from '../../../../../interfaces/route-planning/location.interface';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { SourceDestinationForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/source-destination-form';
import { eSectionType } from 'src/app/modules/route-planner/enums/route-planning/section-type.enum';
import { switchMap, tap, interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BaseMap } from '../../../../shared/map/base-map.component';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { Overlay } from '@angular/cdk/overlay';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { MapComponent } from '../../../../shared/map/map.component';
import { ScheduleRepository } from 'src/app/modules/route-planner/repositories/schedule.repository';
import { Coordinate } from 'src/app/modules/route-planner/interfaces/location/coordinate.interface';

@Component({
    selector: 'app-source-destination-results-map',
    templateUrl: './source-destination-results-map.component.html',
    styleUrls: ['./source-destination-results-map.component.scss'],
    providers: [MapService],
    standalone: false
})
export class SourceDestinationResultsMapComponent
  extends BaseMap<RoutePlanning>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild(MapComponent)
  override mapComponent?: MapComponent;

  /**
   * When set (e.g. route-tracking overlay), start vehicle polling for this route
   * without waiting for another `_resultSubject` emission.
   */
  @Input() vehiclePollRoute: RoutePlanning | null = null;

  /**
   * Which {@link Section} of type TRANSIT to use for `getNextRouteLocation` / grey segment.
   * When omitted, the first transit leg in the route is used (wrong after boarding a later leg).
   */
  @Input() vehiclePollTransitSectionIndex: number | null = null;

  /**
   * When false (user already on board), only the vehicle marker is updated — no grey
   * “vehicle → boarding stop” segment (misleading once the leg has started).
   */
  @Input() showBoardingApproachSegment = true;

  routesToDisplay: (RoutePlanning & { drawed: boolean })[];
  formValues: SourceDestinationForm;
  routeObjects: { [routeId: string]: H.map.Object[] } = {};
  highlightedRouteId: string;

  // Properties for route location tracking
  private routeLocationInterval: Subscription | undefined;
  /** Cancels in-flight GetNextRouteLocation + polyline when a new poll starts (prevents request storms). */
  private vehicleLocationPollSub?: Subscription;
  /** Skip duplicate map polls for the same stop while the previous HTTP is still in flight. */
  private vehicleLocationPollKeyInFlight: string | null = null;
  /** Last vehicle coords from {@link RouteTrackingService} while {@link vehiclePollRoute} is set. */
  private lastTrackingBusCoordinate: Coordinate | null = null;
  private routeLocationMarkers: H.map.Marker[] = [];
  private currentBusMarker: H.map.Marker | null = null; // Track single bus marker to update position instead of recreating
  /** When set, drawn in addition to the full route as a grey segment (vehicle → departure station) */
  private firstTransitSegmentLineString: H.geo.LineString | null = null;
  private routeSegmentPolylineObjects: H.map.Object[] = [];
  private _mapResultSub?: Subscription;

  constructor(
    favoritesService: FavoritesService,
    resizeService: ResizeService,
    overlay: Overlay,

    protected readonly cd: ChangeDetectorRef,
    protected override readonly routePlanningService: RoutePlanningService,
    public override readonly mapService: MapService,
    private scheduleRepository: ScheduleRepository
  ) {
    super(
      favoritesService,
      resizeService,
      overlay,
      mapService,
      routePlanningService
    );
  }

  override ngOnInit(): void {
    this.routePlanningService._formSubject.subscribe((routePlanning) => {
      this.formValues = routePlanning.formValues as SourceDestinationForm;
      this.routesToDisplay = (routePlanning.results as RoutePlanning[]).map(
        (result) => ({ ...result, drawed: false })
      );
    });
    this.routePlanningService.setMapService(this.mapService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showBoardingApproachSegment'] && this.mapService.map) {
      if (changes['showBoardingApproachSegment'].currentValue === false) {
        this.firstTransitSegmentLineString = null;
        this.drawSegmentOverlayIfSet();
      } else if (
        this.vehiclePollRoute &&
        this.lastTrackingBusCoordinate
      ) {
        this.refreshApproachPolylineForBusCoordinate(
          this.lastTrackingBusCoordinate
        );
      }
    }
    if (
      changes['vehiclePollTransitSectionIndex'] &&
      !changes['vehiclePollTransitSectionIndex'].firstChange &&
      this.mapService.map &&
      this.highlightedRouteId
    ) {
      if (this.vehiclePollRoute) {
        if (this.lastTrackingBusCoordinate) {
          this.refreshApproachPolylineForBusCoordinate(
            this.lastTrackingBusCoordinate
          );
        }
        return;
      }
      this.callGetRouteNextLocation();
    }
  }

  override ngOnDestroy(): void {
    this._mapResultSub?.unsubscribe();
    this._mapResultSub = undefined;

    this.mapService.reset();

    // Clean up the interval when component is destroyed
    if (this.routeLocationInterval) {
      this.routeLocationInterval.unsubscribe();
    }
    this.vehicleLocationPollSub?.unsubscribe();
    this.vehicleLocationPollKeyInFlight = null;
    // Clean up route location markers
    // this.clearRouteLocationMarkers();
  }

  override ngAfterViewInit(): void {
    if (!this.mapService.map && !!this.mapComponent) {
      this.mapService.initializeMap(this.mapComponent.mapDiv, {
        // progressiveLoading: true,
        // enableCaching: true,
        controls: {
          zoom: true,
          myLocation: true,
          scalebar: true,
        },
      });
      (window as any).mapInstance = this.mapService.map;
    } else if (this.mapService.map && !!this.mapComponent) {
      // Reconnect the existing map to the new HTML element to ensure proper display
      // this.mapService.reconnectMapToElement(this.mapComponent.mapDiv);
      // Trigger initialization events when reusing the map
      this._mapInitialized = true;
    }

    const onResult = (result: { type: string; value: RoutePlanning }) => {
      this.updateRoutesBasedOnSelection(result);
    };

    // Only set up subscription if not already initialized
    if (!this._mapInitialized) {
      this._mapResultSub = this.mapService.$mapInitialized
        .pipe(
          tap(() => {
            this._mapInitialized = true;
            this.tryBootstrapVehiclePollRoute();
          }),
          switchMap(() => this.routePlanningService._resultSubject)
        )
        .subscribe({
          next: onResult,
          error: () => {
            this._errLoadingMap = true;
          },
        });
    } else {
      this.tryBootstrapVehiclePollRoute();
      this._mapResultSub = this.routePlanningService._resultSubject.subscribe({
        next: onResult,
        error: () => {
          this._errLoadingMap = true;
        },
      });
    }

    this.cd.detectChanges();
  }

  /**
   * Live vehicle from {@link RouteTrackingService}. When {@link vehiclePollRoute} is set,
   * the service already calls {@link Routes/GetNextRouteLocation} — we only update the marker
   * and (when applicable) the grey approach polyline here to avoid duplicate paired HTTP calls.
   */
  public showVehicleAt(coordinate: Coordinate | null): void {
    if (this.vehiclePollRoute) {
      if (
        coordinate == null ||
        coordinate.lat == null ||
        coordinate.lng == null
      ) {
        this.lastTrackingBusCoordinate = null;
        this.vehicleLocationPollSub?.unsubscribe();
        this.vehicleLocationPollSub = undefined;
        this.vehicleLocationPollKeyInFlight = null;
        this.removeRouteLocationMarkersOnly();
        this.firstTransitSegmentLineString = null;
        this.drawSegmentOverlayIfSet();
        return;
      }
      this.lastTrackingBusCoordinate = coordinate;
      this.updateRouteLocationMarker(coordinate);
      if (this.showBoardingApproachSegment) {
        this.refreshApproachPolylineForBusCoordinate(coordinate);
      } else {
        this.firstTransitSegmentLineString = null;
        this.drawSegmentOverlayIfSet();
      }
      return;
    }

    if (
      coordinate == null ||
      coordinate.lat == null ||
      coordinate.lng == null
    ) {
      this.removeRouteLocationMarkersOnly();
      return;
    }
    this.updateRouteLocationMarker(coordinate);
  }

  /**
   * After tracking context changes (e.g. confirmed boarding), refresh vehicle + segment
   * without waiting for the 10s poll. Pass {@link transitSectionIndexOverride} when the
   * parent has not yet propagated {@link vehiclePollTransitSectionIndex} through change detection.
   */
  public requestImmediateVehicleLocation(
    transitSectionIndexOverride?: number | null
  ): void {
    if (this.vehiclePollRoute) {
      return;
    }
    if (this.mapService.map && this.highlightedRouteId) {
      this.callGetRouteNextLocation(transitSectionIndexOverride, true);
    }
  }

  /** Ensures the tracked route is selected on the map and vehicle polling runs (tracking overlay). */
  private tryBootstrapVehiclePollRoute(): void {
    if (!this.vehiclePollRoute?.routeId || !this.mapService.map) {
      return;
    }
    const existing = this.routesToDisplay ?? [];
    if (!existing.some((r) => r.routeId === this.vehiclePollRoute!.routeId)) {
      this.routesToDisplay = [
        ...existing,
        {
          ...this.vehiclePollRoute,
          drawed: false,
        } as RoutePlanning & { drawed: boolean },
      ];
    }
    this.highlightedRouteId = this.vehiclePollRoute.routeId;
    const route = this.routesToDisplay.find(
      (r) => r.routeId === this.vehiclePollRoute!.routeId
    );
    if (!route) {
      return;
    }
    if (!route.drawed) {
      this.drawRoute(route, true, true);
    } else {
      this.mapService.lookAtData(this.getRouteLocations(route.sections));
    }
    if (this.routeLocationInterval) {
      this.routeLocationInterval.unsubscribe();
      this.routeLocationInterval = undefined;
    }
    // Vehicle position + boarding HTTP are owned by RouteTrackingService; map only renders
    // coordinates pushed via showVehicleAt and refreshes the grey segment (polyline) from them.
  }

  private updateRoutesBasedOnSelection(result: {
    type: string;
    value: RoutePlanning;
  }) {
    if (!result || !this.routesToDisplay) {

      return;
    }

    // Safely find the route, handling the case where result.value might be null
    const route = result.value
      ? this.routesToDisplay.find((r) => r.routeId === result.value.routeId)
      : null;

    if (result.type === 'selected') {
      if (!route) {
        this.drawInitialRoutes();
        this.clearRouteLocationMarkers();
        if (this.routeLocationInterval) {
          this.routeLocationInterval.unsubscribe();
        }
      } else {
        this.routesToDisplay.forEach((routeToDisplay) => {
          if (routeToDisplay.routeId !== route.routeId) {
            this.remoteRouteObjects(routeToDisplay.routeId);
          } else if (!routeToDisplay.drawed) {
            this.drawRoute(routeToDisplay, true, true);
          } else {
            this.mapService.lookAtData(
              this.getRouteLocations(routeToDisplay.sections)
            );
          }
        });
        this.startRouteLocationInterval();

      }
    } else if (
      result.type === 'highlighted' &&
      route && // Make sure route exists
      this.highlightedRouteId !== route.routeId
    ) {
      // Only try to remove highlighted route if it exists
      if (this.highlightedRouteId) {
        this.remoteRouteObjects(this.highlightedRouteId);

        const previousRoute = this.routesToDisplay.find(
          (r) => r.routeId === this.highlightedRouteId
        );

        if (previousRoute) {
          this.drawRoute(previousRoute, false);
        }
      }

      this.remoteRouteObjects(route.routeId);
      this.drawRoute(route, true, false);
      this.highlightedRouteId = route.routeId;
      this.startRouteLocationInterval();
    }
  }


  private getRouteLocations(sections: Section[]): Location[] {
    return sections.reduce<Location[]>((prevLocations, section, index) => {
      prevLocations.push(section.departure.place.location);

      if (index < sections.length - 1) {
        prevLocations.push(section.arrival.place.location);
      }

      return prevLocations;
    }, []);
  }

  private addToRouteObjects(
    routeId: string,
    objects: H.map.Object | H.map.Object[]
  ) {
    this.routeObjects[routeId] = (this.routeObjects[routeId] ?? []).concat(
      objects
    );
  }

  private remoteRouteObjects(routeId: string) {
    if (!routeId) return;

    // Safely remove objects if they exist
    if (this.routeObjects[routeId]) {
      this.mapService.removeObjects(this.routeObjects[routeId]);
      this.routeObjects[routeId] = [];
    }

    // Safely find and update the route
    const route = this.routesToDisplay?.find(
      (route) => route.routeId === routeId
    );
    if (route) {
      route.drawed = false;
    }
  }

  private drawInitialRoutes() {
    if (!!this.routesToDisplay && this.routesToDisplay.length > 0) {
      var locations: Location[] = [];

      if (!this.highlightedRouteId) {
        this.highlightedRouteId = this.routesToDisplay[0].routeId;
      }
      this.routesToDisplay
        .filter((route) => !route.drawed)
        .forEach((route) => {
          const isHighlighted = route.routeId == this.highlightedRouteId;

          this.drawRoute(route, isHighlighted);
          locations = locations.concat(this.getRouteLocations(route.sections));
        });
      this.mapService.lookAtData(locations, false);
    }
  }

  private drawRoute(
    route: RoutePlanning & { drawed: boolean },
    isHighlighted: boolean,
    lookAtRoute?: boolean
  ) {
    const sections = route.sections;

    if (!!isHighlighted) {
      this.drawRouteSections(route.routeId, sections);
    }

    this.drawRoutePolylines(route.routeId, sections, isHighlighted);

    if (!!lookAtRoute) {
      this.mapService.lookAtData(this.getRouteLocations(sections), true);
    }
    route.drawed = true;
  }

  private drawRouteSections(routeId: string, route: Section[]) {
    route.forEach((section, index) => {
      const markers = [];
      var icon: H.map.Icon;

      switch (section.transport.mode) {
        case eTransportMode.PEDESTRIAN:
          icon = MAP_ICONS.location_circle_pedestrian();
          break;

        default:
          icon = MAP_ICONS.location_circle_transit();
          break;
      }

      if (!!section.departure?.place) {
        markers.push(
          this.mapService.addMarker(section.departure.place.location, icon)
        );

        if (section.type == eSectionType.TRANSIT) {
          const markerContent = document.createElement('div');

          markerContent.innerHTML = section.departure.place.name;

          const domIcon = new H.map.DomIcon(markerContent);
          markers.push(
            this.mapService.addDomMarker(
              section.departure?.place.location,
              domIcon
            )
          );
        }
      }

      if (index == route.length - 1 && !!section.arrival?.place) {
        markers.push(
          this.mapService.addMarker(
            route[route.length - 1].arrival.place.location,
            MAP_ICONS.pin_green()
          )
        );
      }

      this.addToRouteObjects(routeId, markers);
    });
  }

  private drawRoutePolylines(
    routeId: string,
    route: Section[],
    activeRoute?: boolean
  ) {
    var numoOfTransitSections = 0;

    route.forEach((section) => {
      const polylines: H.map.Polyline[] = [];
      const lineString = H.geo.LineString.fromFlexiblePolyline(
        section.polyline
      );

      switch (section.transport.mode) {
        case eTransportMode.PEDESTRIAN:
          polylines.push(
            this.mapService.drawPolyline(lineString, {
              data: {},
              style: {
                lineWidth: 10,
                strokeColor: !!activeRoute ? '#8636F7' : '#8FA0B2',
                lineDash: [2, 1],
                lineCap: 'round',
                lineJoin: 'round',
              },
              zIndex: !!activeRoute ? 2 : 1,
            })
          );
          break;

        case eTransportMode.CAR:
          polylines.push(
            this.mapService.drawPolyline(lineString, {
              data: {},
              style: {
                lineWidth: 10,
                strokeColor: '#050505',
              },
              zIndex: !!activeRoute ? 2 : 1,
            })
          );
          polylines.push(
            this.mapService.drawPolyline(lineString, {
              data: {},
              style: {
                lineWidth: 8,
                strokeColor: '#fa9702',
              },
              zIndex: !!activeRoute ? 2 : 1,
            })
          );

          break;
        default:
          polylines.push(
            this.mapService.drawPolyline(lineString, {
              data: {},
              style: {
                lineWidth: 10,
                strokeColor: !!activeRoute ? '#0068F5' : '#8FA0B2',
              },
              zIndex: !!activeRoute ? 2 : 1,
            })
          );

          polylines.push(
            this.mapService.drawPolyline(lineString, {
              data: {},
              style: {
                lineWidth: 8,
                strokeColor: !!activeRoute
                  ? this.getColorForSection(numoOfTransitSections)
                  : '#8FA0B2',
              },
              zIndex: !!activeRoute ? 2 : 1,
            })
          );
          numoOfTransitSections++;
          break;
      }

      this.addToRouteObjects(routeId, polylines);
    });
  }

  private getColorForSection(sectionNumber: number): string {
    const colors: string[] = ['#026bfa', '#026b0d', '#b3aa02', '#9c0010'];
    const index = sectionNumber % colors.length;
    return colors[index];
  }

  /**
   * Returns a LineString that is the segment of the full polyline between the two points
   * (from closest point to vehicle to closest point to departure station).
   */
  private getPolylineSegmentBetweenPoints(
    fullPolyline: string,
    fromLat: number,
    fromLng: number,
    toLat: number,
    toLng: number
  ): H.geo.LineString | null {
    try {
      const lineString = H.geo.LineString.fromFlexiblePolyline(fullPolyline);
      const arr = lineString.getLatLngAltArray();
      if (!arr || arr.length < 6) return null;

      const points: { lat: number; lng: number; index: number }[] = [];
      for (let i = 0; i < arr.length; i += 3) {
        points.push({ lat: arr[i], lng: arr[i + 1], index: i / 3 });
      }

      const distFrom = (i: number) =>
        (points[i].lat - fromLat) ** 2 + (points[i].lng - fromLng) ** 2;
      const distTo = (i: number) =>
        (points[i].lat - toLat) ** 2 + (points[i].lng - toLng) ** 2;

      let fromIdx = 0;
      let toIdx = 0;
      let minFrom = distFrom(0);
      let minTo = distTo(0);
      for (let i = 1; i < points.length; i++) {
        if (distFrom(i) < minFrom) {
          minFrom = distFrom(i);
          fromIdx = i;
        }
        if (distTo(i) < minTo) {
          minTo = distTo(i);
          toIdx = i;
        }
      }

      const startIdx = Math.min(fromIdx, toIdx);
      const endIdx = Math.max(fromIdx, toIdx);
      const segment = new H.geo.LineString();
      for (let i = startIdx; i <= endIdx; i++) {
        segment.pushLatLngAlt(points[i].lat, points[i].lng, 0);
      }
      const segmentArr = segment.getLatLngAltArray();
      return segmentArr && segmentArr.length >= 6 ? segment : null;
    } catch {
      return null;
    }
  }

  private startRouteLocationInterval() {
    if (this.routeLocationInterval) {
      this.routeLocationInterval.unsubscribe();
      this.routeLocationInterval = undefined;
    }
    // Call immediately on start
    this.callGetRouteNextLocation();

    this.routeLocationInterval = interval(10000).subscribe(() => {
      this.callGetRouteNextLocation();
    });
  }

  /**
   * Grey “bus → boarding stop” segment while tracking: uses coords from {@link RouteTrackingService}
   * and only calls {@link ScheduleRepository.getRoutePolylineByRouteDescStopAndTime} (no duplicate
   * {@link ScheduleRepository.getNextRouteLocation}).
   */
  private refreshApproachPolylineForBusCoordinate(busCoord: Coordinate): void {
    if (!this.mapService.map || !this.highlightedRouteId) {
      return;
    }
    const currentRoute = this.routesToDisplay?.find(
      (r) => r.routeId === this.highlightedRouteId
    );
    if (!currentRoute?.sections?.length) {
      return;
    }
    const sections = currentRoute.sections;
    const idx = this.vehiclePollTransitSectionIndex;
    let transitSection: Section | undefined;
    if (
      idx != null &&
      idx >= 0 &&
      idx < sections.length &&
      sections[idx].type === eSectionType.TRANSIT
    ) {
      transitSection = sections[idx];
    } else {
      transitSection = sections.find(
        (section) => section.type === eSectionType.TRANSIT
      );
    }
    if (
      !transitSection?.departure?.place?.location ||
      !transitSection.departure.hourTimeString
    ) {
      return;
    }
    const routeDesc = transitSection.transport.description;
    const stopCode = transitSection.departure.place.code;
    const departureTime = transitSection.departure.hourTimeString;

    this.vehicleLocationPollSub?.unsubscribe();
    this.vehicleLocationPollSub = this.scheduleRepository
      .getRoutePolylineByRouteDescStopAndTime(
        routeDesc,
        stopCode,
        departureTime
      )
      .subscribe({
        next: (polylineResponse) => {
          if (
            polylineResponse.success &&
            polylineResponse.data &&
            busCoord.lat != null &&
            busCoord.lng != null
          ) {
            const departureLocation = transitSection.departure!.place.location;
            const segment = this.getPolylineSegmentBetweenPoints(
              polylineResponse.data,
              busCoord.lat,
              busCoord.lng,
              departureLocation.lat,
              departureLocation.lng
            );
            this.firstTransitSegmentLineString = segment;
          } else {
            this.firstTransitSegmentLineString = null;
          }
          this.drawSegmentOverlayIfSet();
        },
        error: () => {
          this.drawSegmentOverlayIfSet();
        },
      });
  }

  private callGetRouteNextLocation(
    transitSectionIndexOverride?: number | null,
    force = false
  ) {
    if (this.vehiclePollRoute) {
      return;
    }
    const currentRoute = this.routesToDisplay?.find(
      (route) => route.routeId === this.highlightedRouteId
    );

    if (!currentRoute?.sections?.length) return;

    const sections = currentRoute.sections;
    let transitSection: Section | undefined;

    const idx =
      transitSectionIndexOverride !== undefined
        ? transitSectionIndexOverride
        : this.vehiclePollTransitSectionIndex;
    if (
      idx != null &&
      idx >= 0 &&
      idx < sections.length &&
      sections[idx].type === eSectionType.TRANSIT
    ) {
      transitSection = sections[idx];
    } else {
      transitSection = sections.find(
        (section) => section.type === eSectionType.TRANSIT
      );
    }
    if (!transitSection) return;

    const routeDesc = transitSection.transport.description;
    const stopCode = transitSection.departure.place.code;
    const departureTime = transitSection.departure.hourTimeString;

    const pollKey = `${routeDesc}::${stopCode}`;
    if (!force && this.vehicleLocationPollKeyInFlight === pollKey) {
      return;
    }

    this.vehicleLocationPollSub?.unsubscribe();
    this.vehicleLocationPollKeyInFlight = pollKey;
    const pollBatch = new Subscription();
    this.vehicleLocationPollSub = pollBatch;

    pollBatch.add(
      this.scheduleRepository
        .getNextRouteLocation(routeDesc, stopCode)
        .pipe(
          finalize(() => {
            if (this.vehicleLocationPollKeyInFlight === pollKey) {
              this.vehicleLocationPollKeyInFlight = null;
            }
          })
        )
        .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.updateRouteLocationMarker(response.data);
            if (!this.showBoardingApproachSegment) {
              this.firstTransitSegmentLineString = null;
              this.drawSegmentOverlayIfSet();
              return;
            }
            pollBatch.add(
              this.scheduleRepository
                .getRoutePolylineByRouteDescStopAndTime(
                  routeDesc,
                  stopCode,
                  departureTime
                )
                .subscribe({
                  next: (polylineResponse) => {
                    if (
                      polylineResponse.success &&
                      polylineResponse.data &&
                      response.data
                    ) {
                      const departureLocation =
                        transitSection.departure.place.location;
                      const segment = this.getPolylineSegmentBetweenPoints(
                        polylineResponse.data,
                        response.data.lat,
                        response.data.lng,
                        departureLocation.lat,
                        departureLocation.lng
                      );
                      this.firstTransitSegmentLineString = segment;
                    } else {
                      this.firstTransitSegmentLineString = null;
                    }
                    this.drawSegmentOverlayIfSet();
                  },
                  error: () => {
                    this.drawSegmentOverlayIfSet();
                  },
                })
            );
          } else {
            // No vehicle position: remove marker so the map does not show a stale bus.
            this.removeRouteLocationMarkersOnly();
            this.firstTransitSegmentLineString = null;
            this.drawSegmentOverlayIfSet();
          }
        },
        error: () => {
          this.removeRouteLocationMarkersOnly();
          this.firstTransitSegmentLineString = null;
          this.drawSegmentOverlayIfSet();
        },
      })
    );
  }

  private redrawCurrentRoute() {
    const currentRoute = this.routesToDisplay?.find(
      (route) => route.routeId === this.highlightedRouteId
    );
    if (!currentRoute) return;
    this.remoteRouteObjects(currentRoute.routeId);
    this.drawRoute(currentRoute, true, false);
    this.drawSegmentOverlayIfSet();
  }

  private removeSegmentOverlay() {
    if (this.routeSegmentPolylineObjects.length) {
      this.mapService.removeObjects(this.routeSegmentPolylineObjects);
      this.routeSegmentPolylineObjects = [];
    }
  }

  /** Removes only the vehicle location markers from the map (keeps segment overlay). */
  private removeRouteLocationMarkersOnly() {
    this.routeLocationMarkers.forEach(marker => {
      this.mapService.removeObject(marker);
    });
    this.routeLocationMarkers = [];
    this.currentBusMarker = null; // Clear reference when removing markers
  }

  private drawSegmentOverlayIfSet() {
    this.removeSegmentOverlay();
    if (
      !this.firstTransitSegmentLineString ||
      (this.firstTransitSegmentLineString.getLatLngAltArray?.()?.length ?? 0) < 6
    ) {
      return;
    }
    const polyline = this.mapService.drawPolyline(
      this.firstTransitSegmentLineString,
      {
        data: {},
        style: {
          lineWidth: 10,
          strokeColor: '#8FA0B2',
        },
        zIndex: 3,
      }
    );
    this.routeSegmentPolylineObjects.push(polyline);
  }

  private clearRouteLocationMarkers() {
    this.firstTransitSegmentLineString = null;
    this.removeSegmentOverlay();
    this.lastTrackingBusCoordinate = null;
    this.routeLocationMarkers.forEach(marker => {
      this.mapService.removeObject(marker);
    });
    this.routeLocationMarkers = [];
    this.currentBusMarker = null; // Clear reference when clearing all markers
  }

  private updateRouteLocationMarker(location: Coordinate) {
    if (!this.mapService.map) {
      console.warn('Map not initialized, cannot add route location marker');
      return;
    }

    if (!location || location.lat == null || location.lng == null) {
      console.warn('Invalid location coordinates:', location);
      return;
    }

    // If marker already exists, update its position instead of recreating
    if (this.currentBusMarker) {
      this.currentBusMarker.setGeometry(new H.geo.Point(location.lat, location.lng));
      this.currentBusMarker.setData({ type: 'route_location', location });
      return;
    }

    // Create new marker only if it doesn't exist
    const marker = new H.map.Marker(
      { lat: location.lat, lng: location.lng },
      {
        data: { type: 'route_location', location },
        icon: MAP_ICONS.on_map(), // Using the same icon as search line map
      }
    );

    // Add click event listener if needed
    marker.addEventListener('tap', (ev) => {
      ev.stopPropagation();
    });

    // Add marker to map and track it
    this.mapService.addObject(marker);
    this.routeLocationMarkers.push(marker);
    this.currentBusMarker = marker; // Store reference for future updates
  }
}
